import { DestroyRef, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, EMPTY, map, Observable, of, ReplaySubject, switchMap, take, tap } from 'rxjs';
import {
  CreateSubOrganizationDTO,
  CreateSubOrganizationResponseDTO,
  DeactivatedSubOrganization,
  DeactivateSuborganizationsResponseDTO,
  EditSubOrganizationDTO,
  EditSubOrganizationResponseDTO,
  FurtherQualificationLevel3RequestDto,
  FurtherQualificationStatus,
  OrganizationBookingApprovalDetails,
  OrganizationBookingApprovalDetailsUpdate,
  SubOrgaExtraOptions,
  SubOrganizationData,
} from '@markant-platform/shared/organization/model';
import { AppConfigService, Page } from '@markant-platform/shared/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { SortAttributes } from '@markant-platform/shared/table/model';
import { generateJsonPatchOperations } from '@markant-platform/shared/util-helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Address, OrganizationDetailData } from 'data-model';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly organizationApiUrl: string = AppConfigService.appConfiguration.apiUrl['organizationService'];
  private readonly registrationApiUrl: string = AppConfigService.appConfiguration.apiUrl['registration'];
  private readonly organization$: ReplaySubject<OrganizationDetailData | undefined> = new ReplaySubject<
    OrganizationDetailData | undefined
  >(1);
  private readonly furtherQualificationApiUrl = AppConfigService.appConfiguration.apiUrl['furtherQualification'];

  constructor(
    private readonly http: HttpClient,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly destroyRef: DestroyRef
  ) {
    this.fetchOrganization()
      .pipe(takeUntilDestroyed())
      .subscribe(organization => this.organization$.next(organization));
  }

  public fetchOrganization(): Observable<OrganizationDetailData> {
    return this.authenticatedUserService.getOrganizationId().pipe(
      take(1),
      switchMap(currentOrganizationId =>
        this.getOrganizationDetailsByIdWithFurtherQualificationStatus(currentOrganizationId)
      ),
      tap(organization => this.organization$.next(organization)),
      takeUntilDestroyed(this.destroyRef),
      catchError(_ => {
        this.organization$.next(undefined);
        return EMPTY;
      })
    );
  }

  public getCurrentOrganization(): Observable<OrganizationDetailData | undefined> {
    return this.organization$;
  }

  public getSubOrganizations(
    organizationId: string,
    pageIndex: number,
    pageSize: number,
    extraOptions: SubOrgaExtraOptions = {}
  ): Observable<Page<SubOrganizationData>> {
    // without passing subOrganizationId it gets all locations of the first level.
    // when passing subOrganizationId it gets all locations of the respective sub organization.
    // Note, that organizationId can be a parentId of a suborganization.
    const subOrganizationParam = extraOptions['subOrganizationId']
      ? `&suborganizationId=${extraOptions['subOrganizationId']}`
      : '';
    const searchQueryParam = extraOptions['searchQuery'] ? `&search=${extraOptions['searchQuery']}` : '';
    const isAscending = extraOptions['sortDirection'] === 'asc';
    const sortQueryParam = `&nameAscending=${extraOptions.sortDirection ? isAscending : true}`;
    const flat = extraOptions['flat'] ? '&flat=true' : '';
    return this.http.get<Page<SubOrganizationData>>(
      `${this.organizationApiUrl}/${organizationId}/suborganization?index=${pageIndex}&size=${pageSize}${sortQueryParam}${subOrganizationParam}${searchQueryParam}${flat}`
    );
  }

  public getSubOrganizationDetailsById(
    organizationId: string,
    subOrganizationId?: string
  ): Observable<SubOrganizationData> {
    return this.http.get<SubOrganizationData>(
      `${this.organizationApiUrl}/${organizationId}/suborganization/${subOrganizationId}`
    );
  }

  /**
   * Create new location or only validate the new location.
   *
   * @param createDTO
   * @param organizationId
   * @param validationOnly
   * @returns {Observable<CreateSubOrganizationResponseDTO>}
   */
  public createSubOrganization(
    createDTO: CreateSubOrganizationDTO,
    organizationId?: string,
    validationOnly = false
  ): Observable<CreateSubOrganizationResponseDTO> {
    return this.authenticatedUserService
      .getOrganizationId()
      .pipe(
        switchMap(currentOrganizationId =>
          this.http.post<CreateSubOrganizationResponseDTO>(
            `${this.organizationApiUrl}/${organizationId ?? currentOrganizationId}/suborganization${validationOnly ? '?validationOnly=true' : ''}`,
            createDTO
          )
        )
      );
  }

  /**
   * Edit a location. Only validation is performed, if validationOnly flag is true.
   * In DTO should contain only properties, which we want to update.
   *
   * @param subOrganizationId
   * @param editDTO
   * @param orgId
   * @param validationOnly
   * @returns {Observable<EditSubOrganizationResponseDTO>}
   */
  public editSubOrganization(
    subOrganizationId: string,
    editDTO: EditSubOrganizationDTO,
    orgId?: string,
    validationOnly = false
  ): Observable<EditSubOrganizationResponseDTO> {
    return this.authenticatedUserService
      .getOrganizationId()
      .pipe(
        switchMap(currentOrganizationId =>
          this.http.patch<EditSubOrganizationResponseDTO>(
            `${this.organizationApiUrl}/${orgId ?? currentOrganizationId}/suborganization/${subOrganizationId}${
              validationOnly ? '?validationOnly=true' : ''
            }`,
            editDTO
          )
        )
      );
  }

  public getOrganizationDetailsById(organizationId: string): Observable<OrganizationDetailData> {
    return this.http.get<OrganizationDetailData>(`${this.organizationApiUrl}/${organizationId}`);
  }

  public getOrganizationBookingApprovalDetailsById(
    organizationId: string
  ): Observable<OrganizationBookingApprovalDetails> {
    return this.http.get<OrganizationBookingApprovalDetails>(
      `${this.organizationApiUrl}/${organizationId}/booking-approval-details`
    );
  }

  private getOrganizationDetailsByIdWithFurtherQualificationStatus(
    organizationId: string
  ): Observable<OrganizationDetailData> {
    return this.http.get<OrganizationDetailData>(
      `${this.organizationApiUrl}/${organizationId}?with-further-qualification-information=true`
    );
  }

  public setOrganizationDetailsById(
    id: string,
    organizationMasterData: OrganizationDetailData,
    updatedOrganizationMasterData: OrganizationDetailData,
    auditReason?: string
  ): Observable<OrganizationDetailData> {
    const updatedOrganizationDetailData: {
      address?: Address;
      phone?: string | null;
      salesTaxId?: string | null;
      taxNumber?: string | null;
      changeReason?: string | null;
    } = {
      ...organizationMasterData,
      ...updatedOrganizationMasterData,
      changeReason: auditReason,
    };

    this.setEmptyStringPropertiesToNull(updatedOrganizationDetailData);

    const body = generateJsonPatchOperations(organizationMasterData, updatedOrganizationDetailData);

    if (body.length === 0) {
      return of(organizationMasterData);
    }
    return this.http
      .patch<OrganizationDetailData>(`${this.organizationApiUrl}/${id}?version=${organizationMasterData.version}`, body)
      .pipe(tap(organization => this.organization$.next(organization)));
  }

  public setOrganizationBookingApprovalDetailsById(
    id: string,
    body: OrganizationBookingApprovalDetailsUpdate
  ): Observable<OrganizationBookingApprovalDetails> {
    return this.http
      .put<OrganizationBookingApprovalDetails>(`${this.organizationApiUrl}/${id}/booking-approval-details`, body)
      .pipe(tap(organization => this.organization$.next(organization)));
  }

  private setEmptyStringPropertiesToNull(obj: any): void {
    for (const prop in obj) {
      if (typeof obj[prop] === 'string' && (obj[prop] as string).trim() === '') {
        obj[prop] = null;
      } else if (typeof obj[prop] === 'object' && obj[prop] !== null) {
        this.setEmptyStringPropertiesToNull(obj[prop]);
      }
    }
  }

  public getOrganizationsForPlatformAdmin(
    pageIndex: number,
    pageSize: number,
    query?: string,
    filters?: string,
    sort?: SortAttributes
  ): Observable<Page<OrganizationDetailData>> {
    const url = filters ? `${this.organizationApiUrl}?${filters}` : this.organizationApiUrl;
    const params = new HttpParams({
      fromObject: {
        index: pageIndex,
        size: pageSize,
        ...(sort && {
          'sort-field': sort.sortField,
          'sort-order': sort.sortOrder,
        }),
        ...(query && { search: query }),
      },
    });

    return this.http.get<Page<OrganizationDetailData>>(`${url}`, { params });
  }

  /**
   * Deactivate locations and their subordinate locations.
   * 'withFailure' flag tells whether the request was successful or not.
   *
   * @param subOrganizations
   * @param organizationId
   * @returns {Observable<DeactivateSuborganizationsResponseDTO>}
   */
  public deactivateSuborganizations(
    subOrganizations: DeactivatedSubOrganization[],
    organizationId?: string
  ): Observable<DeactivateSuborganizationsResponseDTO> {
    return this.authenticatedUserService
      .getOrganizationId()
      .pipe(
        switchMap(currentOrganizationId =>
          this.http.delete<DeactivateSuborganizationsResponseDTO>(
            `${this.organizationApiUrl}/${organizationId ?? currentOrganizationId}/suborganization`,
            { body: { suborganizationsToDeactivate: subOrganizations } }
          )
        )
      );
  }

  public removeOrganization(organizationId: string): Observable<OrganizationDetailData> {
    return this.http.post<OrganizationDetailData>(`${this.organizationApiUrl}/${organizationId}/deactivate`, {});
  }

  getOrganizationId(): Observable<string> {
    return this.authenticatedUserService.getOrganizationId();
  }

  getFurtherQualificationRequestForOrganization(organizationId: string): Observable<FurtherQualificationStatus> {
    return this.http
      .get<{
        furtherQualificationRequest: FurtherQualificationStatus;
      }>(`${this.furtherQualificationApiUrl}/organization/${organizationId}`)
      .pipe(map(request => request.furtherQualificationRequest));
  }

  createFurtherQualificationRequestToLevel3(
    furtherQualificationRequestDto: FurtherQualificationLevel3RequestDto
  ): Observable<FurtherQualificationLevel3RequestDto> {
    return this.http.post<FurtherQualificationLevel3RequestDto>(
      `${this.furtherQualificationApiUrl}`,
      furtherQualificationRequestDto
    );
  }

  resendVerificationMail(platformRequestId: string): Observable<void> {
    return this.http.post<void>(`${this.registrationApiUrl}/${platformRequestId}/resend-verification-email`, {});
  }
}
